import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityLogItem } from '@karehero/models';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AdminTab } from '@karehero/llama/pwa/pages/AdminCareCircleView/AdminCareCircleView';

import AdminTablePage from '@karehero/llama/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';
import { ActivityLog } from '@karehero/llama/pwa/pages/AdminCareCircleView/ActivityLog';

import {
  useGetAccountCaseloadQuery,
  useGetCaseloadQuery,
  useCreateAccountCaseloadMutation,
  useRemoveCaseMutation,
  useGetCaseloadActivityQuery,
} from 'store/api/careManagement';
import { useGetAllAccountQuery } from 'store/api/account';
import { getAccount } from 'store/slices/iamSlice';
import { CaseWithDetails } from '@karehero/models';
import { DYNAMIC_ROUTES } from 'router';
import { newToast } from 'store/slices/uiSlice';
import { AppDispatch } from 'store';
import { Chip, Tab, Tabs } from '@mui/material';

enum CaseloadTab {
  Cases = 'Cases',
  Activity = 'Activity',
}

const columns: GridColDef[] = [
  {
    field: 'careRecipientName',
    headerName: 'Care Recipient',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'primaryCaregiverName',
    headerName: 'Primary Caregiver',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'primaryCaregiverEmail',
    headerName: 'Email',
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'isPrimary',
    headerName: 'Role',
    width: 120,
    renderCell: ({ value }: { value: boolean }) =>
      value ? (
        <Chip label='Primary' color='primary' size='small' />
      ) : (
        <Chip label='Watcher' size='small' variant='outlined' />
      ),
  },
  {
    field: 'addedAt',
    headerName: 'Added',
    width: 120,
    valueGetter: (value: string) =>
      value ? new Date(value).toLocaleDateString() : '',
  },
];

export const AdminCaseloadManager = () => {
  const { accountId: accountIdParam } = useParams<{ accountId: string }>();
  const account = useSelector(getAccount);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const accountId = accountIdParam || account?.id;
  const isOwnCaseload = accountId === account?.id;

  useEffect(() => {
    if (!accountIdParam && account?.id) {
      navigate(DYNAMIC_ROUTES.ADMIN_CASELOAD_FOR(account.id), {
        replace: true,
      });
    }
  }, [accountIdParam, account?.id, navigate]);

  // TODO[pagination]: replace with useGetAccountQuery({ accountId })
  const { data: allAccountsData } = useGetAllAccountQuery(
    { perPage: 1000 },
    { skip: isOwnCaseload },
  );
  const targetAccount = useMemo(() => {
    if (isOwnCaseload) return account;
    return allAccountsData?.data?.find((a) => a.id === accountId);
  }, [isOwnCaseload, account, allAccountsData, accountId]);

  const {
    data: caseloadMeta,
    isFetching: isMetaFetching,
    isError: isMetaError,
  } = useGetAccountCaseloadQuery(
    { accountId: accountId ?? '' },
    { skip: !accountId },
  );

  const [createCaseload] = useCreateAccountCaseloadMutation();
  const hasAttemptedCreate = useRef(false);

  useEffect(() => {
    if (isMetaError && accountId && !hasAttemptedCreate.current) {
      hasAttemptedCreate.current = true;
      createCaseload({ accountId }).then((res) => {
        if ('error' in res) {
          dispatch(
            newToast({
              title: 'Error',
              description: 'Failed to create caseload.',
              variant: 'error',
            }),
          );
        }
      });
    }
  }, [isMetaError, accountId, createCaseload, dispatch]);

  const {
    data: caseloadData,
    isFetching: isCasesFetching,
    isError: isCasesError,
  } = useGetCaseloadQuery(
    { caseloadId: caseloadMeta?.id ?? '' },
    { skip: !caseloadMeta?.id },
  );

  const PAGE_SIZE = 20;
  const [activityOffset, setActivityOffset] = useState(0);
  const [allActivityItems, setAllActivityItems] = useState<ActivityLogItem[]>(
    [],
  );

  const {
    data: activityPage,
    isFetching: isActivityLoading,
    refetch: refetchActivity,
  } = useGetCaseloadActivityQuery(
    {
      caseloadId: caseloadMeta?.id ?? '',
      limit: PAGE_SIZE,
      offset: activityOffset,
    },
    { skip: !caseloadMeta?.id },
  );

  useEffect(() => {
    if (!activityPage) return;
    setAllActivityItems((prev) => {
      const next =
        activityOffset === 0 ? activityPage : [...prev, ...activityPage];
      const seen = new Set<string>();
      return next.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
    });
  }, [activityPage, activityOffset]);

  const hasMoreActivity = (activityPage?.length ?? 0) >= PAGE_SIZE;

  const handleLoadMoreActivity = useCallback(() => {
    setActivityOffset((prev) => prev + PAGE_SIZE);
  }, []);

  const handleRefreshActivity = useCallback(() => {
    setActivityOffset(0);
    setAllActivityItems([]);
  }, []);

  const activityTabMap: Record<string, AdminTab> = {
    action_completed: AdminTab.Actions,
    form_completed: AdminTab.Forms,
    shortlist_item_secured: AdminTab.CareSearch,
    document_scanned: AdminTab.Overview,
  };

  const getActivityItemHref = useCallback((item: ActivityLogItem) => {
    if (!item.careCircleId) return undefined;
    const tab = activityTabMap[item.activityType];
    const base = DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(item.careCircleId);
    return tab ? `${base}?tab=${encodeURIComponent(tab)}` : base;
  }, []);

  const [currentTab, setCurrentTab] = useState(CaseloadTab.Cases);

  const [removeCase] = useRemoveCaseMutation();

  const isLoading =
    !accountId ||
    isMetaFetching ||
    (!caseloadMeta && !isMetaError) ||
    isCasesFetching;
  const isError = isCasesError;

  const rows = useMemo(() => {
    if (!caseloadData?.cases) return [];
    return caseloadData.cases;
  }, [caseloadData]);

  const handleRemove = useCallback(
    async (row: CaseWithDetails) => {
      if (!caseloadData?.id) return;
      const res = await removeCase({
        caseloadId: caseloadData.id,
        caseId: row.id,
      });
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to remove from caseload.',
            variant: 'error',
          }),
        );
        return;
      }
      dispatch(
        newToast({
          title: 'Removed',
          description: 'Care circle removed from caseload.',
        }),
      );
    },
    [caseloadData?.id, removeCase, dispatch],
  );

  const targetName = targetAccount
    ? `${targetAccount.firstName} ${targetAccount.lastName}`.trim()
    : '';
  const title = isOwnCaseload
    ? 'My Caseload'
    : targetName
      ? `${targetName}'s Caseload`
      : 'Caseload';

  return (
    <AdminTablePage title={title}>
      <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)}>
        {Object.values(CaseloadTab).map((t) => (
          <Tab key={t} label={t} value={t} />
        ))}
      </Tabs>

      {currentTab === CaseloadTab.Cases && (
        <DataTable
          columns={columns}
          rows={rows}
          isLoading={isLoading}
          isError={isError}
          noDataMsg='No care circles in this caseload yet.'
          actions={[
            {
              label: 'View Circle',
              icon: 'eye',
              onPress: (row: CaseWithDetails) => {
                navigate(
                  DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(row.careCircleId),
                );
              },
            },
            {
              label: 'Remove from Caseload',
              icon: 'trash',
              onPress: handleRemove,
            },
          ]}
        />
      )}

      {currentTab === CaseloadTab.Activity && (
        <ActivityLog
          items={allActivityItems}
          isLoading={isActivityLoading}
          hasMore={hasMoreActivity}
          onLoadMore={handleLoadMoreActivity}
          onRefresh={handleRefreshActivity}
          onCareCircleClick={(id) =>
            navigate(DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(id))
          }
          getItemHref={getActivityItemHref}
          onItemClick={(item) => {
            const href = getActivityItemHref(item);
            if (href) navigate(href);
          }}
        />
      )}
    </AdminTablePage>
  );
};

export default AdminCaseloadManager;
